import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';

const categoryIcons: Record<string, string> = {
  'Shoes': 'ri-footprint-line',
  'Hoodies/Sweaters': 'ri-t-shirt-line',
  'T-Shirts': 'ri-shirt-line',
  'Jackets': 'ri-t-shirt-2-line',
  'Pants/Shorts': 'ri-checkbox-blank-line',
  'Headwear': 'ri-customer-service-2-line',
  'Sets': 'ri-stack-line',
  'Underwear/Underpants': 'ri-checkbox-blank-line',
  'Jersey': 'ri-user-star-line',
  'Accessories': 'ri-vip-diamond-line',
  'Bags': 'ri-handbag-line',
  'Belts': 'ri-link',
  'Perfumes': 'ri-drop-line',
  'Other Stuff': 'ri-more-line',
};

export default function HeroSection() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  return (
    <header className="relative w-full min-h-screen flex flex-col">
      {/* Background image */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://readdy.ai/api/search-image?query=Artistic%20editorial%20flat%20lay%20with%20diverse%20fashion%20items%20arranged%20organically%20on%20warm%20ivory%20linen%20surface%2C%20woven%20straw%20tote%20bag%20next%20to%20terracotta%20leather%20sandals%20and%20sage%20green%20linen%20scarf%2C%20soft%20natural%20window%20light%20casting%20gentle%20diagonal%20shadows%2C%20mix%20of%20textures%20including%20woven%20raffia%20smooth%20leather%20and%20soft%20cotton%2C%20warm%20neutral%20palette%20with%20muted%20earthy%20accents%2C%20lifestyle%20product%20photography%20with%20intentional%20negative%20space%20and%20relaxed%20composition&width=1600&height=900&seq=hero-bg-2026-v2&orientation=landscape"
          alt="Superbuy Spreadsheet fashion product discovery platform"
          title="Superbuy Spreadsheet - Fashion Product Discovery Platform"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background-50/55 via-background-50/25 to-background-50/90"></div>
      </div>

      {/* Navbar */}
      <nav className="relative z-20 w-full">
        <div className="flex items-center justify-between px-6 md:px-12 py-5">
          <a href="/" className="flex items-center gap-3 cursor-pointer">
            <div className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center">
              <i className="ri-store-2-line text-background-50 text-lg"></i>
            </div>
            <span className="font-heading text-xl font-semibold text-primary-800 whitespace-nowrap">SuperBuySpread</span>
          </a>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-8">
            <a href="/" className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap">Home</a>
            <a href="/superbuy-spreadsheet-guide" className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap">Guide</a>
            <a href="/comparison" className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap">Comparison</a>
            <a href="/is-superbuy-spreadsheet-safe" className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap">Safety</a>
            <a href="#categories" className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap">Categories</a>

            {/* Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-2 px-4 py-2 rounded-full border border-foreground-300/40 bg-background-50/70 backdrop-blur-sm text-sm font-medium text-foreground-800 hover:border-foreground-400/60 transition-all cursor-pointer whitespace-nowrap"
              >
                <span>All Categories</span>
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute right-0 mt-3 w-64 bg-background-50/95 backdrop-blur-md rounded-2xl border border-background-200/80 py-3 z-50">
                  <div className="max-h-80 overflow-y-auto">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-5 py-2.5 text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors group whitespace-nowrap"
                      >
                        <i className={`${categoryIcons[cat.name] || 'ri-price-tag-3-line'} text-foreground-400 text-base`}></i>
                        <span>{cat.name}</span>
                        <i className="ri-arrow-right-s-line text-foreground-400 opacity-0 group-hover:opacity-100 transition-opacity ml-auto"></i>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer relative z-50"
            aria-label="Toggle menu"
          >
            <i className={`text-2xl text-foreground-800 transition-all duration-300 ${mobileMenuOpen ? 'ri-close-line rotate-90' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </nav>

      {/* Full-screen Mobile Menu Overlay */}
      <div className={`md:hidden fixed inset-0 z-40 bg-background-50/98 backdrop-blur-xl transition-all duration-500 ${mobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}>
        <div className="h-full flex flex-col pt-20 pb-6 px-6 overflow-y-auto">
          {/* Main nav links */}
          <div className="flex flex-col gap-1 mb-8">
            <a href="/" onClick={() => setMobileMenuOpen(false)} className="group flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-background-100 transition-colors cursor-pointer">
              <div className="w-9 h-9 rounded-full bg-background-100 flex items-center justify-center">
                <i className="ri-home-4-line text-foreground-700 text-lg"></i>
              </div>
              <span className="text-base font-medium text-foreground-900">Home</span>
              <i className="ri-arrow-right-s-line text-foreground-400 ml-auto group-hover:translate-x-1 transition-transform"></i>
            </a>
            <a href="/superbuy-spreadsheet-guide" onClick={() => setMobileMenuOpen(false)} className="group flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-background-100 transition-colors cursor-pointer">
              <div className="w-9 h-9 rounded-full bg-background-100 flex items-center justify-center">
                <i className="ri-book-open-line text-foreground-700 text-lg"></i>
              </div>
              <span className="text-base font-medium text-foreground-900">Complete Guide</span>
              <i className="ri-arrow-right-s-line text-foreground-400 ml-auto group-hover:translate-x-1 transition-transform"></i>
            </a>
            <a href="/comparison" onClick={() => setMobileMenuOpen(false)} className="group flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-background-100 transition-colors cursor-pointer">
              <div className="w-9 h-9 rounded-full bg-background-100 flex items-center justify-center">
                <i className="ri-scales-3-line text-foreground-700 text-lg"></i>
              </div>
              <span className="text-base font-medium text-foreground-900">Comparison</span>
              <i className="ri-arrow-right-s-line text-foreground-400 ml-auto group-hover:translate-x-1 transition-transform"></i>
            </a>
            <a href="/is-superbuy-spreadsheet-safe" onClick={() => setMobileMenuOpen(false)} className="group flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-background-100 transition-colors cursor-pointer">
              <div className="w-9 h-9 rounded-full bg-background-100 flex items-center justify-center">
                <i className="ri-shield-check-line text-foreground-700 text-lg"></i>
              </div>
              <span className="text-base font-medium text-foreground-900">Safety Guide</span>
              <i className="ri-arrow-right-s-line text-foreground-400 ml-auto group-hover:translate-x-1 transition-transform"></i>
            </a>
          </div>

          {/* Divider */}
          <div className="border-t border-background-200/60 pt-6">
            <p className="text-xs font-semibold tracking-widest uppercase text-foreground-500 mb-4 px-1">
              <i className="ri-grid-line mr-1.5"></i>Shop by Category
            </p>
            <div className="grid grid-cols-2 gap-2">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileMenuOpen(false)}
                  className="group flex items-center gap-2.5 px-3 py-3 rounded-xl hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <div className="w-8 h-8 rounded-full bg-primary-100/70 flex items-center justify-center flex-shrink-0">
                    <i className={`${categoryIcons[cat.name] || 'ri-price-tag-3-line'} text-primary-600 text-sm`}></i>
                  </div>
                  <div className="min-w-0">
                    <span className="text-sm text-foreground-800 group-hover:text-foreground-950 transition-colors block truncate">{cat.name}</span>
                  </div>
                </a>
              ))}
            </div>
          </div>

          {/* Bottom CTA in mobile menu */}
          <div className="mt-auto pt-6 border-t border-background-200/60">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center justify-center gap-2 w-full py-3.5 bg-primary-500 text-background-50 font-semibold text-sm rounded-full hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
            >
              <span>SUPERBUY SHEET</span>
              <i className="ri-arrow-right-up-line"></i>
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center justify-center gap-2 w-full mt-3 py-3.5 bg-accent-500 text-background-50 font-semibold text-sm rounded-full hover:bg-accent-600 transition-all cursor-pointer whitespace-nowrap"
            >
              <span>BUY IT NOW</span>
              <i className="ri-shopping-bag-line"></i>
            </a>
          </div>
        </div>
      </div>

      {/* Hero Content */}
      <div className="relative z-10 flex-1 flex items-center">
        <div className="w-full px-6 md:px-12">
          <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
            {/* Left content */}
            <div className="flex-1 text-center lg:text-left max-w-xl">
              <span className="inline-block text-xs font-medium tracking-widest uppercase text-accent-600 bg-accent-100/60 px-3 py-1 rounded-full mb-6 whitespace-nowrap">
                Fashion Discovery Platform
              </span>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-foreground-950 leading-tight mb-6">
                Superbuy Sheet<br />
                <span className="text-foreground-600 font-normal">Shopping Intelligence</span>
              </h1>
              <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-8 max-w-lg mx-auto lg:mx-0">
                Discover curated fashion products across 14 categories. The Superbuy Spreadsheet organizes shoes, clothing, accessories and more — helping you find exactly what you want, faster.
              </p>

              {/* Dual CTAs */}
              <div className="flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="group flex items-center gap-3 px-8 py-3.5 bg-primary-500 text-background-50 font-semibold text-sm rounded-full hover:bg-primary-600 transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  <span>SUPERBUY SHEET</span>
                  <span className="w-6 h-6 rounded-full border border-background-50/30 flex items-center justify-center group-hover:translate-x-0.5 transition-transform">
                    <i className="ri-arrow-right-up-line text-xs"></i>
                  </span>
                </a>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="group flex items-center gap-3 px-8 py-3.5 bg-accent-500 text-background-50 font-semibold text-sm rounded-full hover:bg-accent-600 transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  <span>BUY IT NOW</span>
                  <span className="w-6 h-6 rounded-full border border-background-50/30 flex items-center justify-center group-hover:translate-x-0.5 transition-transform">
                    <i className="ri-shopping-bag-line text-xs"></i>
                  </span>
                </a>
              </div>

              {/* Trust indicators */}
              <div className="flex items-center gap-6 mt-8 justify-center lg:justify-start text-xs text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-checkbox-circle-line text-secondary-500 text-sm"></i>
                  Updated Weekly
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-checkbox-circle-line text-secondary-500 text-sm"></i>
                  14 Categories
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-checkbox-circle-line text-secondary-500 text-sm"></i>
                  Curated Picks
                </span>
              </div>
            </div>

            {/* Right visual */}
            <div className="hidden lg:block flex-1">
              <div className="relative w-full aspect-[4/5] max-w-md mx-auto rounded-3xl overflow-hidden bg-background-100/80">
                <img
                  src="https://readdy.ai/api/search-image?query=Beautiful%20artisan%20leather%20tote%20bag%20with%20terracotta%20suede%20sandals%20and%20woven%20straw%20hat%20arranged%20on%20warm%20ivory%20linen%20draped%20over%20wooden%20stool%2C%20soft%20natural%20light%20filtering%20through%20sheer%20curtain%2C%20warm%20earthy%20neutral%20tones%20with%20sage%20green%20fabric%20accent%2C%20editorial%20lifestyle%20product%20photography%20with%20relaxed%20organic%20composition%20and%20gentle%20shadows&width=800&height=1000&seq=hero-side-2026-v2&orientation=portrait"
                  alt="Superbuy Spreadsheet curated fashion products"
                  title="Superbuy Spreadsheet - Curated Fashion Selection"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="relative z-10 flex justify-center pb-8">
        <a href="#categories" className="flex flex-col items-center gap-2 text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer">
          <span className="text-xs font-medium tracking-widest uppercase">Discover</span>
          <i className="ri-arrow-down-line text-lg animate-bounce"></i>
        </a>
      </div>
    </header>
  );
}